import React, { useEffect, useRef, useState } from 'react';
import { Button, Dimmer, Loader } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { b64toBlob, formatFileSize } from '~/utils/helpers';
import Word from '@assets/images/Word.png';
import Excel from '@assets/images/Excel.png';
import Documents from '@assets/images/Documents.png';
import notify from '../Notify';
import { CloudStorageService } from '~/api/services/cloudStorage';
import { imageTypes, pdfType, videoTypes } from '~/Constants';
import { FileUpload } from './style';
import { AcceptInputProp } from '~/types';

interface FileUploadProps {
  onChange?: (file: File) => void;
  id?: string
  value?: any | File
  activeTabIndex?: number
  sizeLimitInMB?: number
  accept?: AcceptInputProp[]
  mimeTypes?: any
  removeButton?: () => void
  removeDetails?: boolean
}
const ONE_MILLION = 1000000;

/// this components needs to refactored
// url to get file must be dynamic
// value needs to be properly defined

const SimpleFileUpload: React.FC<FileUploadProps> = (props) => {
  const { onChange, mimeTypes, removeDetails } = props
  const [file, setFile] = useState<File | null>(props.value);
  const { t } = useTranslation()
  const [previewUrl, setPreviewUrl] = useState<string | ArrayBuffer | null>(null);
  const [loadingFile, setLoadingFile] = useState(false)
  const refIframe = useRef(null)

  useEffect(() => {
    if (props.value?.fileEdit?.src) {
      setPreviewUrl(props.value.fileEdit?.src)
      setFile({
        name: props.value?.fileEdit?.filename,
        type: getType(props.value?.fileEdit?.type)
      } as File)
    }
    else if (props.value?.filename) {
      fetchFileFromNameValue(props.value)
    }

  }, [props.activeTabIndex, props.id])

  function getType(type: string) {
    if (imageTypes.find(typeI => typeI === type)) {
      return `image/${type}`
    } else if (videoTypes.find(typeI => typeI === type)) {
      return `video/${type}`
    } else if (pdfType === type) {
      return `application/${type}`
    } else if (type === 'xlxs' || type === 'docx') {
      return `application/${type}`
    }
    return type
  }

  async function fetchFileFromNameValue(fileValue: any) {
    setLoadingFile(true);
    try {
      const response = await CloudStorageService.getTempLinkForFile(props.id, { fileName: fileValue?.filename })
      setPreviewUrl(response.data);
      setFile({
        name: fileValue?.filename,
        size: fileValue?.filesize,
        type: getType(fileValue?.filetype)
      } as File)
    } catch (error) {
      setLoadingFile(false);
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingFile(false);
  }

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const pickFileHandler = () => {
    fileInputRef.current?.click();
  };

  function applyPickedFileAsPreview(pickedFile: File) {
    onChange(pickedFile)
    setFile(pickedFile);
    const fileReader = new FileReader();
    fileReader.onload = () => {
      if (pickedFile.type.startsWith('application/pdf')) {
        const blob = b64toBlob(fileReader.result, pickedFile.type);
        const blobUrl = URL.createObjectURL(blob);
        setPreviewUrl(blobUrl);
      } else {
        setPreviewUrl(fileReader.result);
      }
    };
    if (pickedFile.type.startsWith('image')) {
      fileReader.readAsDataURL(pickedFile);
    } else if (pickedFile.type.startsWith('video')) {
      setPreviewUrl(URL.createObjectURL(pickedFile));
    } else if (pickedFile.type.startsWith('application')) {
      fileReader.readAsDataURL(pickedFile);
    } else {
      fileReader.readAsArrayBuffer(pickedFile);
    }
  }

  const fileChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const pickedFile: File | null = event.target.files?.[0];
    if (!pickedFile) {
      return;
    }
    if (props.sizeLimitInMB && (pickedFile.size / ONE_MILLION) > props.sizeLimitInMB) {
      notify(t('File size is too large'), 'error')
      return
    }
    const separatorSize = pickedFile?.name?.split('.')
    const basicType = pickedFile?.name?.split('.')[separatorSize.length - 1]
    if (mimeTypes && !mimeTypes.find(mimeType => mimeType === basicType)) {
      notify(t('File type not supported'), 'error')
      return
    }
    applyPickedFileAsPreview(pickedFile)
  };

  function renderApplicationDocuments() {
    if (file?.type.startsWith('application/pdf')) {
      if (!file.lastModified) {
        // Encode the PDF URL
        const encodedUrl = encodeURIComponent(previewUrl.toString());
        // Construct the embedded URL
        const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
        return <iframe ref={refIframe} src={embeddedUrl} />
      }
      return <iframe src={previewUrl.toString()} />
    }
    else if (file?.name.includes('.docx')) {
      return <img src={Word} alt='Preview' />
    }
    else if (file?.name.includes('.xlsx')) {
      return <img src={Excel} alt='Preview' />
    }

    return <img src={Documents} alt='Preview' />
  }

  function renderPreview() {
    const basicType = file?.type?.split('/')[0]
    switch (basicType) {
      case 'image':
        return <img src={previewUrl as string} alt='Preview' />
      case 'video':
        return <video src={previewUrl as string} controls />
      case 'application':
        return renderApplicationDocuments()
      default:
        return <img src={Documents} alt='Preview' />
    }
  }

  function handleRefreshClick() {
    {
      if (refIframe.current) {
        refIframe.current.setAttribute('src', '')
        const encodedUrl = encodeURIComponent(previewUrl.toString());
        const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
        refIframe.current.setAttribute('src', embeddedUrl)
      }
    }
  }

  function renderPreviewUrl() {
    return <>
      <FileUpload.ImageUploadPreview>
        {!removeDetails && <FileUpload.LabelWrapper>
          <FileUpload.FileInfoLabel>
            {t('File name: {{fileName}}', { fileName: file?.name })}
          </FileUpload.FileInfoLabel>
          {file?.size && <FileUpload.FileInfoLabel>
            {t('File size: {{fileSize}}', { fileSize: formatFileSize(file?.size) })}
          </FileUpload.FileInfoLabel>}
        </FileUpload.LabelWrapper>}
        {file?.type?.startsWith('application/pdf') && (props.value?.fileEdit?.src || !file.lastModified) && <FileUpload.StyledRefreshButton type={'button'}
          onClick={() => handleRefreshClick()}
          basic
          icon={'refresh'}
          content={t('Refresh')} />
        }
        <div className='wrapper'>
          {renderPreview()}
        </div>
      </FileUpload.ImageUploadPreview>
    </>
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (!file) return;
    applyPickedFileAsPreview(file)
  }

  function renderPlaceholderFiles() {
    const supportedTypes = props?.mimeTypes?.join(', ') || '.jpg, .png, .jpeg, .pdf, .mp4...'
    return <FileUpload.ImageUploadPlaceholder onClick={pickFileHandler}>
      <Dimmer inverted active={loadingFile}>
        <Loader active={loadingFile} />
      </Dimmer>
      <div>
        <div style={{ textAlign: 'center' }}>{t('Click to select a file')}</div>
        <div>{t('Supported file types: {{supportedTypes}}', { supportedTypes: props?.accept?.join(',') || supportedTypes })}</div>
      </div>
    </FileUpload.ImageUploadPlaceholder>
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.add('drag-over')
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('drag-over')
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('drag-over')
  };

  return (
    <FileUpload.ImageUploadContainer
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {props.removeButton && previewUrl && <Button
        className='remove-button'
        onClick={() => {
          props.removeButton()
          onChange(null)
          setFile(null)
          setPreviewUrl('')
        }
        }
        size={'small'}
        basic
        color='red'
        type={'button'}
        icon='close'
      />}
      <FileUpload.FileInput
        ref={fileInputRef}
        type='file'
        accept={props?.accept?.join(',') || 'all'}
        onChange={fileChangeHandler}
      />
      {previewUrl ? renderPreviewUrl() : renderPlaceholderFiles()}
      <FileUpload.ImageUploadButton onClick={pickFileHandler}>{t('Pick file')}</FileUpload.ImageUploadButton>
    </FileUpload.ImageUploadContainer>
  )
};
export default SimpleFileUpload;

